import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Badge } from '@voilajsx/uikit/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@voilajsx/uikit/dialog';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Coins, Loader2, AlertTriangle, CheckCircle, ArrowRight, ShoppingCart } from 'lucide-react';
import { Header,  SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { config } from '../../auth/config';

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price: number;
  isActive: boolean;
}

const BuyCreditsPage: React.FC = () => {
  const { isAuthenticated, token } = useAuth();

  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [supportPhone, setSupportPhone] = useState('');
  const [supportWhatsapp, setSupportWhatsapp] = useState('');

  // Dialog state
  const [selectedPack, setSelectedPack] = useState<CreditPack | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState<{ success: boolean; message: string; paymentId?: string } | null>(null);
  const [simulating, setSimulating] = useState(false);

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
    [config.auth.headers.auth]: `Bearer ${token}`,
  }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch packs (no auth needed)
      const packsRes = await fetch(`${config.api.baseUrl}/api/payment/packs`, {
        headers: {
          [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
        }
      });
      if (!packsRes.ok) throw new Error(`HTTP ${packsRes.status}: ${packsRes.statusText}`);
      const packsJson = await packsRes.json();
      const packsData = packsJson.data || packsJson;
      setPacks(packsData?.packs || packsData || []);

      // Fetch public settings for support contact
      try {
        const settingsRes = await fetch(`${config.api.baseUrl}/api/settings/public`, {
          headers: { [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue },
        });
        if (settingsRes.ok) {
          const sJson = await settingsRes.json();
          const s = sJson.data || sJson;
          setSupportPhone(s.supportPhone || '');
          setSupportWhatsapp(s.supportWhatsappUrl || '');
        }
      } catch { /* non-critical */ }

      // Fetch balance if authenticated
      if (isAuthenticated && token) {
        try {
          const balanceRes = await fetch(`${config.api.baseUrl}/api/credit/balance`, { headers: authHeaders() });
          if (balanceRes.ok) {
            const balJson = await balanceRes.json();
            const balData = balJson.data || balJson;
            setBalance(balData?.balance ?? balData?.credits ?? 0);
          }
        } catch {
          // Balance fetch failure is non-critical
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load credit packs');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, authHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Find the best value pack (lowest price per credit)
  const bestValuePackId = React.useMemo(() => {
    const activePacks = packs.filter((p) => p.isActive);
    if (activePacks.length === 0) return null;
    let best = activePacks[0];
    for (const pack of activePacks) {
      if (pack.price / pack.credits < best.price / best.credits) {
        best = pack;
      }
    }
    return best.id;
  }, [packs]);

  const handleBuyClick = (pack: CreditPack) => {
    setSelectedPack(pack);
    setPurchaseResult(null);
    setDialogOpen(true);
  };

  const handlePurchase = async () => {
    if (!selectedPack || !token) return;

    setPurchasing(true);
    setPurchaseResult(null);

    try {
      const res = await fetch(`${config.api.baseUrl}/api/payment/create-order`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ creditPackId: selectedPack.id }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${res.status}: ${res.statusText}`);
      }
      const json = await res.json();
      const data = json.data || json;

      const paymentId = data?.paymentId || data?.orderId || data?.id;
      setPurchaseResult({
        success: true,
        message: 'Order created! Payment integration coming soon. For testing, ask admin to simulate payment.',
        paymentId
      });
    } catch (err: any) {
      setPurchaseResult({
        success: false,
        message: err.message || 'Failed to create order'
      });
    } finally {
      setPurchasing(false);
    }
  };

  const handleSimulatePayment = async () => {
    if (!purchaseResult?.paymentId || !token) return;

    setSimulating(true);

    try {
      const simRes = await fetch(`${config.api.baseUrl}/api/payment/simulate-success`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ paymentId: purchaseResult.paymentId }),
      });
      if (!simRes.ok) {
        const errData = await simRes.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${simRes.status}: ${simRes.statusText}`);
      }

      setPurchaseResult({
        success: true,
        message: 'Payment simulated successfully! Your credits have been added.'
      });

      // Refresh balance
      try {
        const balanceRes = await fetch(`${config.api.baseUrl}/api/credit/balance`, { headers: authHeaders() });
        if (balanceRes.ok) {
          const balJson = await balanceRes.json();
          const balData = balJson.data || balJson;
          setBalance(balData?.balance ?? balData?.credits ?? 0);
        }
      } catch {
        // ignore
      }
    } catch (err: any) {
      setPurchaseResult({
        success: false,
        message: err.message || 'Failed to simulate payment'
      });
    } finally {
      setSimulating(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedPack(null);
    setPurchaseResult(null);
  };

  if (!isAuthenticated) {
    return (
      <PageLayout size="full">
        <SEO title="Buy Credits" description="Purchase credit packs for plot designs" />
        <Header />
        <PageLayout.Content>
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Please{' '}
              <Link to="/auth/login" className="font-medium text-primary hover:underline">
                sign in
              </Link>{' '}
              to purchase credits.
            </AlertDescription>
          </Alert>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  return (
    <PageLayout size="full">
      <SEO
        title="Buy Credits"
        description="Purchase credit packs for your plot designs"
      />
      <Header />

      <PageLayout.Content>
        <div className="space-y-6 pb-20">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Top Up Wallet</h1>
            <p className="text-muted-foreground">
              Choose a top-up pack to add balance to your wallet
            </p>
          </div>

          {/* Current Balance */}
          <div className="flex items-center gap-3 rounded-lg border border-zinc-700 bg-card p-4">
            <Coins className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium">Current Balance:</span>
            {loading ? (
              <div className="h-6 w-16 bg-muted rounded animate-pulse" />
            ) : (
              <span className="text-lg font-bold">₹{balance}</span>
            )}
          </div>

          {/* Error state */}
          {error && (
            <Alert className="bg-destructive/10 border-destructive text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Loading state */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-6 w-32 bg-muted rounded" />
                  </CardHeader>
                  <CardContent>
                    <div className="h-10 w-20 bg-muted rounded mb-2" />
                    <div className="h-5 w-24 bg-muted rounded mb-4" />
                    <div className="h-10 w-full bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Credit Pack Cards */}
          {!loading && !error && packs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {packs.filter((p) => p.isActive).map((pack) => {
                const isBestValue = pack.id === bestValuePackId;

                return (
                  <Card key={pack.id} className={`relative flex flex-col ${isBestValue ? 'border-primary ring-2 ring-primary/20' : ''}`}>
                    {isBestValue && (
                      <div className="absolute -top-3 left-4">
                        <Badge className="bg-primary text-primary-foreground">
                          Best Value
                        </Badge>
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle>{pack.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-between gap-4">
                      <div>
                        <p className="text-4xl font-bold">
                          <span className="text-2xl align-top">₹</span>{pack.credits}
                        </p>
                        <p className="text-sm text-muted-foreground">added to your balance</p>
                      </div>
                      <Button
                        onClick={() => handleBuyClick(pack)}
                        className="w-full"
                        variant={isBestValue ? 'default' : 'outline'}
                      >
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Buy
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && packs.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground text-lg">
                  No credit packs available at the moment. Please check back later.
                </p>
              </CardContent>
            </Card>
          )}
          {/* Contact CTA */}
          <div style={{
            background: 'rgba(255,217,61,0.06)', border: '1px solid rgba(255,217,61,0.2)',
            borderRadius: 12, padding: '20px 24px',
          }}>
            <p style={{ fontWeight: 600, color: '#FFD93D', marginBottom: 4, fontSize: 15 }}>
              Need credits added directly?
            </p>
            <p style={{ color: '#aaa', fontSize: 13, marginBottom: 16 }}>
              Reach out to our team and we'll top up your wallet.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <p style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Contact</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Sai Ram</p>
                <p style={{ fontSize: 13, color: '#aaa', marginTop: 2 }}>
                  {supportPhone || '+91 79893 58436'}
                </p>
              </div>
              <a
                href={supportWhatsapp || 'https://wa.me/917989358436'}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: '#25D366', color: '#fff', textDecoration: 'none',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </PageLayout.Content>


      {/* Purchase Confirmation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Purchase</DialogTitle>
            {selectedPack && !purchaseResult && (
              <DialogDescription>
                You're purchasing {selectedPack.name}: ₹{selectedPack.credits} will be added to your balance
              </DialogDescription>
            )}
          </DialogHeader>

          {purchaseResult && (
            <div className="py-2">
              <Alert className={purchaseResult.success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-destructive/10 border-destructive text-destructive'}>
                {purchaseResult.success ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                <AlertDescription className={purchaseResult.success ? 'text-green-700' : 'text-destructive/80'}>
                  {purchaseResult.message}
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            {!purchaseResult && (
              <>
                <Button variant="outline" onClick={closeDialog} disabled={purchasing}>
                  Cancel
                </Button>
                <Button onClick={handlePurchase} disabled={purchasing}>
                  {purchasing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Proceed to Payment
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </>
            )}

            {purchaseResult?.success && purchaseResult.paymentId && (
              <Button
                variant="secondary"
                onClick={handleSimulatePayment}
                disabled={simulating}
              >
                {simulating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Simulating...
                  </>
                ) : (
                  'Simulate Payment (Dev)'
                )}
              </Button>
            )}

            {purchaseResult && (
              <Button variant="outline" onClick={closeDialog}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default BuyCreditsPage;
